import { Http } from "@encoway/sales-showroom-auth"

import { ResponseError } from "../ResponseError"
import { Constants } from "../constants"
import { Selection } from "./selection"
import {
    Characteristic,
    Language,
    ProductGroupResult,
    ProductGroupsResult,
    ProductResult,
    SelectProductsResult
} from "./types/response"

/**
 * Handle exception from catalog service.
 * @param response {Object} the response
 * @return response.json() or throws an ResponseError
 * @private
 */
function handleException(response: Response): Promise<any> {
    if (response.ok !== true) {
        throw new ResponseError(`Error: ${response.statusText}`, response)
    }

    return response.json()
}

/**
 * Handle exception from catalog service.
 * @param json {Object} the response from the service
 * @return json or throws an ResponseError
 * @private
 */
function handleServiceException(json: any): any {
    if (json.message && json.type) {
        throw new ResponseError(json.message, json)
    }
    return json
}

/**
 * The Catalog Service REST API implementation.
 *
 * @class CatalogService
 * @param http {Http} The http object
 * @param baseUrl {String} The base url, e.g. http://localhost:8080/showcase/
 * @param {String} [lang] the optional language (default de-DE)
 */
export class CatalogService {
    private http: Http
    private baseUrl: string
    private lang: string

    constructor(http: Http, baseUrl: string, lang: string) {
        this.http = http
        this.baseUrl = baseUrl + "/api/catalog"
        this.lang = lang || Constants.Locale.Default
    }

    /**
     * Select the language to be used in queries.
     * @param {String} [lang] the optional language (default de-DE)
     * @return this
     */
    useLanguage(lang: string): this {
        this.lang = lang || Constants.Locale.Default
        return this
    }

    /**
     * Retrieve all languages
     * @param {String} [lang] the optional language (default de-DE)
     * @return a promise
     */
    languages(lang?: string): Promise<Language[]> {
        const fetchParams = {
            method: "GET",
            headers: new Headers({
                "Accept-Language": lang || this.lang
            })
        }

        return this.http
            .fetch(`${this.baseUrl}/languages/`, fetchParams)
            .then(handleException)
            .then(handleServiceException)
    }

    /**
     * Gets the characteristics
     * @param {String} [lang] the optional language (default de-DE)
     * @return a promise
     */
    characteristics(lang?: string): Promise<Characteristic[]> {
        const fetchParams = {
            method: "GET",
            headers: new Headers({ "Accept-Language": lang || this.lang })
        }

        return this.http
            .fetch(`${this.baseUrl}/characteristics/`, fetchParams)
            .then(handleException)
            .then(handleServiceException)
    }

    /**
     * Gets the characteristic specification
     * @param {String} id the characteristic id
     * @param {String} [lang] the optional language (default de-DE)
     * @return a promise
     */
    characteristic(id: string, lang?: string): Promise<Characteristic> {
        const fetchParams = {
            method: "GET",
            headers: new Headers({ "Accept-Language": lang || this.lang })
        }

        return this.http
            .fetch(`${this.baseUrl}/characteristics/${id}`, fetchParams)
            .then(handleException)
            .then(handleServiceException)
    }

    /**
     * Search for a term.
     * @param term {String} the search String
     * @param {String} [lang] the optional language (default de-DE)
     */
    search(term: string, lang?: string): Promise<ProductResult> {
        const query = {
            search: {
                term
            }
        }

        const fetchParams = {
            method: "POST",
            headers: new Headers({
                "Content-Type": "application/json",
                Accept: "application/json",
                "Accept-Language": lang || this.lang
            }),
            body: JSON.stringify(query)
        }

        return this.http
            .fetch(`${this.baseUrl}/products/_selection`, fetchParams)
            .then(handleException)
            .then(handleServiceException)
    }

    /**
     * @deprecated use subgroups instead
     * Get the product group.
     * @param group {String} the product group id
     * @param {String} [lang] the optional language (default de-DE)
     * @return a promise
     */
    productGroup(group: string, lang?: string): Promise<ProductGroupsResult> {
        const fetchParams = {
            method: "GET",
            headers: new Headers({
                "Accept-Language": lang || this.lang
            })
        }
        if (group === Constants.ProductGroup.Root) {
            return this.http
                .fetch(`${this.baseUrl}/productgroups/`, fetchParams)
                .then((response) => response.json())
        }

        return this.http
            .fetch(
                `${this.baseUrl}/productgroups/${group}/productgroups`,
                fetchParams
            )
            .then(handleException)
            .then(handleServiceException)
    }

    /**
     * Get the product group information.
     * @param group {String} the product group id
     * @param lang {String} the optional language (default de-DE)
     * @return a promise
     */
    group(group: string, lang?: string): Promise<ProductGroupResult> {
        const fetchParams = {
            method: "GET",
            headers: new Headers({
                "Accept-Language": lang || this.lang
            })
        }
        return this.http
            .fetch(
                `${this.baseUrl}/productgroups/${encodeURIComponent(group)}`,
                fetchParams
            )
            .then(handleException)
            .then(handleServiceException)
    }

    /**
     * Get the product subgroups.
     * @param group {String} the product group id
     * @param lang {String} the optional language (default de-DE)
     * @return a promise
     */
    subgroups(group: string, lang?: string): Promise<ProductGroupsResult> {
        const fetchParams = {
            method: "GET",
            headers: new Headers({
                "Accept-Language": lang || this.lang
            })
        }
        if (group === Constants.ProductGroup.Root) {
            return this.http
                .fetch(`${this.baseUrl}/productgroups/`, fetchParams)
                .then((response) => response.json())
        }
        return this.http
            .fetch(
                `${this.baseUrl}/productgroups/${encodeURIComponent(
                    group
                )}/productgroups`,
                fetchParams
            )
            .then(handleException)
            .then(handleServiceException)
    }

    /**
     * Select products according to the given filter.
     * @param {Selection} selection the selection options
     * @param {String} [lang] the optional language (default de-DE)
     * @return a promise
     */
    products(
        selection: Selection,
        lang?: string
    ): Promise<SelectProductsResult> {
        const fetchParams = {
            method: "POST",
            headers: new Headers({
                "Content-Type": "application/json",
                Accept: "application/json",
                "Accept-Language": lang || this.lang
            }),
            body: JSON.stringify(selection.build())
        }

        return this.http
            .fetch(`${this.baseUrl}/products/_selection`, fetchParams)
            .then(handleException)
            .then(handleServiceException)
            .then((result) => {
                if (result?.metadata?.total) {
                    const { metadata, ...other } = result
                    return {
                        ...other,
                        metadata: {
                            totalCount: metadata.total.hitCount,
                            ...metadata
                        }
                    }
                }
                return result
            })
    }

    /**
     * Get the product
     * @param {String} id the product id
     * @param {String} [lang] the optional language (default de-DE)
     */
    product(id: string, lang?: string): Promise<ProductResult> {
        const fetchParams = {
            method: "GET",
            headers: new Headers({
                "Accept-Language": lang || this.lang
            })
        }

        return this.http
            .fetch(
                `${this.baseUrl}/products/${encodeURIComponent(id)}`,
                fetchParams
            )
            .then(handleException)
            .then(handleServiceException)
    }
}
